import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import {
  ArrowLeft, Shield, CheckCircle, ScanLine,
  Lock, AlertTriangle, Loader2, FileText,
} from 'lucide-react';
import { toast } from 'sonner';
import { useEmbeddedMode, postEmbeddedStepComplete } from '../hooks/useEmbeddedMode';
import IdBarcodeScanner from '../components/IdBarcodeScanner';

/**
 * iter 97 — Identity Verification page.
 *
 * Steps:
 *  1. Intro — concise bullet list, CTA "Verify with my State ID".
 *  2. Scan — camera + PDF417 decoder for the back of the ID.
 *  3. Confirm — server-parsed fields displayed for user review.
 *  4. Submit — POST /identity/submit-id, lock fields, advance.
 *
 * Security:
 *  - All barcode decoding happens in the browser (@zxing/browser).
 *    The camera frames never leave the device.
 *  - Server re-parses the AAMVA payload — FE-supplied fields are
 *    rejected; barcode is the source of truth.
 *  - Fields are locked once submitted (id_verified_at). Only
 *    admins can override.
 */
const STEP_INTRO = 'intro';
const STEP_SCAN = 'scan';
const STEP_CONFIRM = 'confirm';
const STEP_DONE = 'done';

const FieldRow = ({ label, value }) => (
  <div className="flex justify-between items-start gap-3 py-2.5 border-b border-[#dfdfdf] dark:border-[#2a2a2a] last:border-0">
    <span className="text-xs uppercase tracking-wide text-[#676767]">{label}</span>
    <span className="text-sm font-medium text-[#1c1a1c] text-right">{value || '—'}</span>
  </div>
);

const IdentityVerificationPage = () => {
  const navigate = useNavigate();
  const { user, api, refreshUser } = useAuth();
  const embedded = useEmbeddedMode();
  const [step, setStep] = useState(STEP_INTRO);
  const [parsing, setParsing] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [parsed, setParsed] = useState(null);   // server-returned parsed fields
  const [rawBarcode, setRawBarcode] = useState(''); // held in memory only; cleared on success
  const [scanError, setScanError] = useState(null);

  // If already verified, jump to the done state immediately.
  useEffect(() => {
    if (user?.id_verified_at || user?.step2b_identity_verified) {
      setStep(STEP_DONE);
    }
  }, [user?.id_verified_at, user?.step2b_identity_verified]);

  const handleScanned = async (raw) => {
    setRawBarcode(raw);
    setParsing(true);
    setScanError(null);
    try {
      const r = await api.post('/identity/parse-id-barcode', { barcode: raw });
      if (!r.data?.valid) {
        setScanError(r.data?.warnings?.[0] || "Couldn't read your ID. Please try again.");
        setStep(STEP_SCAN); // stay on scan
        setRawBarcode('');
        return;
      }
      setParsed(r.data);
      setStep(STEP_CONFIRM);
    } catch (e) {
      const detail = e.response?.data?.detail || 'Could not parse ID. Please try again.';
      setScanError(detail);
      setRawBarcode('');
    } finally {
      setParsing(false);
    }
  };

  const handleSubmit = async () => {
    if (!rawBarcode || !parsed) return;
    setSubmitting(true);
    try {
      await api.post('/identity/submit-id', {
        barcode: rawBarcode,
        confirmed_first_name: parsed.id_first_name,
        confirmed_last_name: parsed.id_last_name,
      });
      // Clear sensitive raw barcode from memory ASAP
      setRawBarcode('');
      setParsed(null);
      await refreshUser();
      setStep(STEP_DONE);
      toast.success('Identity verified.');
      if (embedded) postEmbeddedStepComplete('step2b_identity_verified');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Verification failed. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const restart = () => {
    setRawBarcode('');
    setParsed(null);
    setScanError(null);
    setStep(STEP_SCAN);
  };

  // iter166 — Embedded popup layout — matches the NDA + MFA popups:
  // light-mode only, hero (olive icon chip + bold title), warm
  // off-white body card, olive-accent CTA footer.
  if (embedded) {
    const oliveBtn = "w-full h-12 rounded-xl flex items-center justify-center gap-2 text-base shadow-sm bg-[#757150] hover:bg-[#5c5840] text-white disabled:opacity-60";
    const oliveOutline = "w-full h-11 rounded-xl flex items-center justify-center gap-2 text-sm bg-white border border-[#ece9df] text-[#1c1a1c] hover:border-[#c9c4a8]";

    if (step === STEP_DONE) {
      return (
        <div className="min-h-screen flex flex-col bg-white text-[#1c1a1c]">
          <div className="px-6 pt-8 pb-4 flex flex-col items-center text-center">
            <div className="w-16 h-16 bg-[#e8ede6] rounded-2xl flex items-center justify-center mb-4 shadow-sm">
              <CheckCircle className="w-8 h-8 text-[#757150]" />
            </div>
            <h2 className="text-2xl font-bold text-[#1c1a1c]">Verification Successful</h2>
          </div>
          <div className="px-6 flex-1 flex flex-col justify-center">
            <div className="rounded-2xl bg-[#fafaf7] border border-[#ece9df] p-5 shadow-sm">
              <p className="text-[14.5px] text-[#3a3a3a] leading-[1.6]">
                Your identity is confirmed. Your name, date of birth, and address are now locked
                on your profile.
              </p>
            </div>
          </div>
        </div>
      );
    }

    if (step === STEP_CONFIRM && parsed) {
      return (
        <div className="min-h-screen flex flex-col bg-white text-[#1c1a1c]">
          <div className="px-6 pt-8 pb-4 flex flex-col items-center text-center">
            <div className="w-16 h-16 bg-[#e8ede6] rounded-2xl flex items-center justify-center mb-4 shadow-sm">
              <CheckCircle className="w-8 h-8 text-[#757150]" />
            </div>
            <h2 className="text-2xl font-bold text-[#1c1a1c]">Confirm your details</h2>
          </div>
          <div className="px-6 flex-1 flex flex-col justify-center">
            <div className="rounded-2xl bg-[#fafaf7] border border-[#ece9df] p-5 shadow-sm" data-testid="id-verify-confirm-fields">
              <p className="text-[13px] text-[#676767] mb-2 leading-snug">
                We read these fields from your ID. Confirm they're correct before continuing.
              </p>
              <FieldRow label="First Name" value={parsed.id_first_name} />
              {parsed.id_middle_name ? <FieldRow label="Middle Name" value={parsed.id_middle_name} /> : null}
              <FieldRow label="Last Name" value={parsed.id_last_name} />
              <FieldRow label="Date of Birth" value={parsed.id_dob} />
              <FieldRow label="Address" value={[parsed.id_address, parsed.id_city, parsed.id_state, parsed.id_postal_code].filter(Boolean).join(', ')} />
              <FieldRow label="ID State" value={parsed.id_state} />
              <FieldRow label="License #" value={parsed.id_license_number} />
              {parsed.id_expiry ? <FieldRow label="Expires" value={parsed.id_expiry} /> : null}
            </div>
          </div>
          <div className="px-6 pt-4 pb-6 bg-white space-y-2" style={{ paddingBottom: 'max(1.5rem, env(safe-area-inset-bottom))' }}>
            <Button className={oliveBtn} onClick={handleSubmit} disabled={submitting} data-testid="id-verify-submit-btn">
              {submitting ? (<><Loader2 className="w-4 h-4 mr-2 animate-spin" />Verifying…</>) : 'These details are correct'}
            </Button>
            <Button className={oliveOutline} onClick={restart} disabled={submitting} data-testid="id-verify-rescan-btn">
              Re-scan
            </Button>
          </div>
        </div>
      );
    }

    if (step === STEP_SCAN) {
      return (
        <div className="min-h-screen flex flex-col bg-white text-[#1c1a1c]">
          <div className="px-6 pt-8 pb-4 flex flex-col items-center text-center">
            <div className="w-16 h-16 bg-[#e8ede6] rounded-2xl flex items-center justify-center mb-4 shadow-sm">
              <ScanLine className="w-8 h-8 text-[#757150]" />
            </div>
            <h2 className="text-2xl font-bold text-[#1c1a1c]">Scan the back of your ID</h2>
          </div>
          <div className="px-6 flex-1 flex flex-col justify-center">
            <div className="rounded-2xl bg-[#fafaf7] border border-[#ece9df] p-4 shadow-sm" data-testid="id-verify-scan">
              <p className="text-[13px] text-[#676767] mb-3 leading-snug">
                Point the camera at the barcode on the back of your driver's license or state ID.
                Hold steady — it scans automatically.
              </p>
              <IdBarcodeScanner onScan={handleScanned} onCancel={() => setStep(STEP_INTRO)} />
              {parsing && (
                <div className="mt-3 flex items-center gap-2 text-sm text-[#676767]">
                  <Loader2 className="w-4 h-4 animate-spin" /> Reading your ID…
                </div>
              )}
              {scanError && (
                <div className="mt-3 bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-900 flex gap-2 items-start" data-testid="id-verify-scan-error">
                  <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                  <span>{scanError}</span>
                </div>
              )}
            </div>
          </div>
        </div>
      );
    }

    // Intro
    return (
      <div className="min-h-screen flex flex-col bg-white text-[#1c1a1c]">
        <div className="px-6 pt-7 pb-3 flex flex-col items-center text-center">
          <div className="w-16 h-16 bg-[#e8ede6] rounded-2xl flex items-center justify-center mb-3 shadow-sm">
            <Shield className="w-8 h-8 text-[#757150]" />
          </div>
          <h2 className="text-2xl font-bold text-[#1c1a1c]">Verify your Identity</h2>
        </div>
        <div className="px-6 flex-1 flex flex-col justify-center">
          <div className="rounded-2xl bg-[#fafaf7] border border-[#ece9df] p-5 shadow-sm">
            <p className="text-[14px] text-[#3a3a3a] leading-[1.6] mb-3.5">
              We verify your identity to protect your account, prevent fraud, and keep every
              community on ORB safe.
            </p>

            <ul className="space-y-3">
              {[
                ['User Protection', "Adds a layer of security so unauthorized users can't pose as you."],
                ['Fraud Prevention', 'Confirms each account represents a real person — reducing impersonation and abuse.'],
                ['Stronger Community', 'Verified members increase the integrity of every community on ORB.'],
              ].map(([title, body]) => (
                <li key={title} className="flex items-start gap-2.5">
                  <CheckCircle className="w-[18px] h-[18px] text-[#757150] flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="font-semibold text-[#1c1a1c] text-[13.5px]">{title}</p>
                    <p className="text-[12.5px] text-[#5a5a5a] leading-[1.5]">{body}</p>
                  </div>
                </li>
              ))}
            </ul>

            <div className="my-4 h-px bg-[#ece9df]" />

            <div className="flex items-start gap-2">
              <Lock className="w-4 h-4 text-[#757150] mt-0.5 flex-shrink-0" />
              <p className="text-[12.5px] text-[#676767] leading-[1.5]">
                Your ID is scanned <span className="font-semibold text-[#1c1a1c]">on your device</span> using your camera. The image never leaves your phone.
              </p>
            </div>
          </div>

          <div className="mt-3.5 rounded-2xl bg-[#f5f5f0] border-l-[3px] border-[#757150] px-4 py-3 flex items-start gap-3">
            <div className="w-8 h-8 rounded-full bg-white flex items-center justify-center flex-shrink-0 shadow-sm">
              <FileText className="w-4 h-4 text-[#757150]" />
            </div>
            <div className="flex-1">
              <p className="text-[13.5px] font-semibold text-[#1c1a1c] mb-0.5">Have your ID ready</p>
              <p className="text-[12.5px] text-[#5a5a5a] leading-[1.5]">
                You'll scan the <span className="font-semibold text-[#1c1a1c]">back</span> of your ID — the side with the barcode.
              </p>
            </div>
          </div>
        </div>
        <div className="px-6 pt-3 pb-5 bg-white" style={{ paddingBottom: 'max(1.25rem, env(safe-area-inset-bottom))' }}>
          <Button className={oliveBtn} onClick={() => setStep(STEP_SCAN)} data-testid="id-verify-start-btn">
            <ScanLine className="w-4 h-4" />
            Verify with my State ID
          </Button>
        </div>
      </div>
    );
  }

  // Non-embedded (standalone /identity-verification route) — original layout preserved.
  return (
    <div
      className="min-h-screen bg-gradient-to-b from-[#d4e4cf] via-[#e8ede6] to-[#c9d4c3] dark:from-[#1a1a1a] dark:via-[#0d0d0d] dark:to-[#000000] p-4"
      style={{ paddingTop: 'max(1rem, env(safe-area-inset-top, 0px))' }}
    >
      <div className="max-w-md mx-auto pt-2">
        {/* Header */}
        <div className="flex items-center gap-4 mb-5">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate('/')}
            className="rounded-full"
            data-testid="back-btn"
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-[#1c1a1c]">Identity Verification</h1>
            <p className="text-sm text-[#676767]">Secure & private — scan happens on your device.</p>
          </div>
        </div>

        {step === STEP_INTRO && (
          <>
            <Card className="bg-white/95 dark:bg-[#141414] border-0 shadow-lg mb-3" data-testid="id-verify-intro">
              <CardContent className="p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 bg-[#e8ede6] dark:bg-[#2a2a2a] rounded-lg flex items-center justify-center">
                    <Shield className="w-5 h-5 text-[#757150]" />
                  </div>
                  <h2 className="text-xl font-bold text-[#1c1a1c]">Why we verify</h2>
                </div>
                <ul className="space-y-3">
                  {[
                    ['User Protection', 'Adds a layer of security so unauthorized users can\u2019t pose as you.'],
                    ['Fraud Prevention', 'Confirms each account represents a real person — reducing impersonation and abuse.'],
                    ['Stronger Community', 'Verified members increase the integrity of every community on ORB.'],
                  ].map(([title, body]) => (
                    <li key={title} className="flex items-start gap-3">
                      <CheckCircle className="w-5 h-5 text-[#757150] flex-shrink-0 mt-0.5" />
                      <div>
                        <p className="font-semibold text-[#1c1a1c] text-sm">{title}</p>
                        <p className="text-sm text-[#676767]">{body}</p>
                      </div>
                    </li>
                  ))}
                </ul>
                <div className="mt-5 pt-4 border-t border-[#dfdfdf] dark:border-[#333] flex gap-2 items-start">
                  <Lock className="w-4 h-4 text-[#757150] mt-0.5 flex-shrink-0" />
                  <p className="text-xs text-[#676767]">
                    Your ID is scanned <span className="font-semibold">on your device</span> using your camera. The image of your ID never leaves your phone.
                  </p>
                </div>
              </CardContent>
            </Card>

            <div className="bg-white/70 dark:bg-[#1a1a1a] rounded-lg p-3.5 mb-3 flex items-start gap-2">
              <FileText className="w-4 h-4 text-[#757150] mt-0.5 flex-shrink-0" />
              <p className="text-sm text-[#1c1a1c]">
                <span className="font-semibold">Have your state ID or driver's license ready.</span> You'll scan the <span className="font-semibold">back</span> (the side with the barcode).
              </p>
            </div>

            <Button
              className="w-full bg-[#2C3E50] hover:bg-[#34495E] text-white h-12 flex items-center justify-center gap-2"
              onClick={() => setStep(STEP_SCAN)}
              data-testid="id-verify-start-btn"
            >
              <ScanLine className="w-4 h-4" />
              Verify with my State ID
            </Button>

            <button
              type="button"
              onClick={() => navigate('/')}
              className="w-full text-center mt-3 text-sm text-[#676767] hover:text-[#1c1a1c] underline"
              data-testid="id-verify-later-btn"
            >
              I'll Do This Later
            </button>
          </>
        )}

        {step === STEP_SCAN && (
          <>
            <Card className="bg-white/95 dark:bg-[#141414] border-0 shadow-lg mb-3" data-testid="id-verify-scan">
              <CardContent className="p-4">
                <h2 className="font-semibold text-[#1c1a1c] mb-1">Scan the back of your ID</h2>
                <p className="text-xs text-[#676767] mb-3">
                  Point the camera at the barcode on the back of your driver's license or state ID. Hold steady — it scans automatically.
                </p>
                <IdBarcodeScanner onScan={handleScanned} onCancel={() => setStep(STEP_INTRO)} />
                {parsing && (
                  <div className="mt-3 flex items-center gap-2 text-sm text-[#676767]">
                    <Loader2 className="w-4 h-4 animate-spin" /> Reading your ID…
                  </div>
                )}
                {scanError && (
                  <div className="mt-3 bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-900 flex gap-2 items-start" data-testid="id-verify-scan-error">
                    <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                    <span>{scanError}</span>
                  </div>
                )}
              </CardContent>
            </Card>
          </>
        )}

        {step === STEP_CONFIRM && parsed && (
          <>
            <Card className="bg-white/95 dark:bg-[#141414] border-0 shadow-lg mb-3" data-testid="id-verify-confirm">
              <CardContent className="p-5">
                <div className="flex items-center gap-2 mb-3">
                  <CheckCircle className="w-5 h-5 text-emerald-600" />
                  <h2 className="font-semibold text-[#1c1a1c]">Confirm your details</h2>
                </div>
                <p className="text-xs text-[#676767] mb-4">
                  We read these fields from your ID. Please confirm they're correct before continuing.
                </p>
                <div data-testid="id-verify-confirm-fields">
                  <FieldRow label="First Name" value={parsed.id_first_name} />
                  {parsed.id_middle_name ? <FieldRow label="Middle Name" value={parsed.id_middle_name} /> : null}
                  <FieldRow label="Last Name" value={parsed.id_last_name} />
                  <FieldRow label="Date of Birth" value={parsed.id_dob} />
                  <FieldRow label="Address" value={[parsed.id_address, parsed.id_city, parsed.id_state, parsed.id_postal_code].filter(Boolean).join(', ')} />
                  <FieldRow label="ID State" value={parsed.id_state} />
                  <FieldRow label="License #" value={parsed.id_license_number} />
                  {parsed.id_expiry ? <FieldRow label="Expires" value={parsed.id_expiry} /> : null}
                </div>
              </CardContent>
            </Card>
            <Button
              className="w-full bg-[#2C3E50] hover:bg-[#34495E] text-white h-12"
              onClick={handleSubmit}
              disabled={submitting}
              data-testid="id-verify-submit-btn"
            >
              {submitting ? (<><Loader2 className="w-4 h-4 mr-2 animate-spin" />Verifying…</>) : 'These details are correct'}
            </Button>
            <Button
              variant="outline"
              className="w-full mt-2 h-12"
              onClick={restart}
              disabled={submitting}
              data-testid="id-verify-rescan-btn"
            >
              Re-scan
            </Button>
          </>
        )}

        {step === STEP_DONE && (
          <Card className="bg-white/95 dark:bg-[#141414] border-0 shadow-lg" data-testid="id-verify-done">
            <CardContent className="p-8 text-center">
              <div className="w-20 h-20 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="w-12 h-12 text-green-600 dark:text-green-400" />
              </div>
              <h2 className="text-2xl font-bold mb-2 text-[#1c1a1c]">Verification Successful</h2>
              <p className="text-[#676767] mb-6">
                Your identity is confirmed. Your name, date of birth, and address are now locked on your profile.
              </p>
              <Button
                className="w-full bg-[#2C3E50] hover:bg-[#34495E] text-white h-12"
                onClick={() => navigate('/')}
                data-testid="return-to-onboarding-btn"
              >
                Return to Onboarding
              </Button>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

export default IdentityVerificationPage;
